/**
 * Adaptador para transformar respuestas de ConsultaRedV2
 * Convierte datos crudos de la API a estructuras tipadas para el componente
 */

export class ConsultaRedV2Adapter {

  /**
   * Adapta las ramas disponibles desde la respuesta V2
   * Filtra solo las que permiten agenda y autogestión
   * @param {array} availableBranchesConsultaRed - Sedes disponibles del servicio
   * @returns {array} - Sedes adaptadas
   */
  adaptBranches(availableBranchesConsultaRed) {
    if (!availableBranchesConsultaRed || availableBranchesConsultaRed.length === 0) {
      return [];
    }

    const filteredBranches = availableBranchesConsultaRed.filter(
      (branch) => branch.Gestion === 'AGENDA' && branch.AutoGestion === 'S'
    );

    const adaptedBranches = filteredBranches.map((branch) => ({
      Address: branch.Direccion,
      AplicativoAgenda: branch.AplicativoAgenda,
      BranchUrl: '',
      Code: branch.CodUnidad,
      Description: '',
      ImageUrl: '',
      Latitude: '',
      Longitude: '',
      NIT: branch.IDPrestador,
      Name: branch.NomSede,
      Pagar: false,
      Phone: '',
      Program: branch.Programa,
      SAP: null,
      TipoPlan: branch.TipoPlan,
      Type: 0,
    }));
    
    const uniqueBranches = adaptedBranches.filter(
      (branch, index, self) =>
        index === self.findIndex((t) => t.Code === branch.Code)
    );

    return uniqueBranches.sort((a, b) => a.Name.localeCompare(b.Name));
  }

  /**
   * Resuelve el médico asignado basado en el agrupador y datos disponibles
   * @param {object} responseData - Respuesta del servicio V2
   * @param {number} agrupador - Código de agrupador (1, 2, 3, etc)
   * @param {string} specialtySelected - Código de especialidad seleccionada
   * @returns {object|null} - Objeto del médico asignado o null
   */
  resolveAssignedDoctor(responseData, agrupador, specialtySelected) {
    if (!responseData || !responseData.Result) {
      return null;
    }

    const { AvailableBranchesConsultaRed, AssignedDoctor } = responseData.Result;

    // Para agrupador 1 (Medicina General, Pediatría, Medicina Familiar)
    // Buscar médico personal primero
    if (agrupador === 1 && AvailableBranchesConsultaRed && AvailableBranchesConsultaRed.length > 0) {
      const personalDoctor = AvailableBranchesConsultaRed.find(
        (e) => e.Servicio === specialtySelected && e.Direccionamiento === 1 && e.TIDMedico === 1
      );

      if (personalDoctor) {
        return this.buildDoctorObject(personalDoctor);
      }
    }

    // Fallback a médico tratante
    return AssignedDoctor || null;
  }

  /**
   * Construye un objeto de médico a partir de datos crudos
   * @param {object} rawDoctorData - Datos crudos del médico
   * @returns {object}
   */
  buildDoctorObject(rawDoctorData) {
    return {
      Availability: 0,
      BranchCode: rawDoctorData.CodSede,
      BranchName: rawDoctorData.NomSede,
      DoctorId: rawDoctorData.IDMedico,
      EPSCode: rawDoctorData.CodEPS,
      FullName: rawDoctorData.NomMedico,
      Gender: null,
      ImageUrl: null,
      NetCode: 0,
      SpecialityCode: rawDoctorData.Servicio,
      SpecialityName: rawDoctorData.NomServicio,
      TeamCode: 0,
      UnitCode: 0,
      UserId: null,
      UserTypeId: rawDoctorData.TIDMedico,
    };
  }

  /**
   * Construye el objeto medicalAvailable para el estado del componente
   * @param {object} responseData - Respuesta del servicio V2
   * @param {array} adaptedBranches - Sedes ya adaptadas
   * @param {string} specialtySelected - Especialidad seleccionada
   * @param {number} agrupador - Código de agrupador
   * @returns {object}
   */
  buildMedicalAvailable(responseData, adaptedBranches, specialtySelected, agrupador) {
    const assignedDoctor = this.resolveAssignedDoctor(responseData, agrupador, specialtySelected);

    return {
      AssignedDoctor: assignedDoctor,
      AvailableBranches: adaptedBranches,
      NonCompliances: null,
      ServiceCode: specialtySelected,
      ShowBranches: true,
      UserHasAssignedAppointments: false,
      UserHasAssignedPersonalDoctor: false,
      UserHasAssignedTreatingDoctor: true,
      UserHasNonCompliances: false,
    };
  }

  /**
   * Determina si hay médico personal asignado
   * @param {object} responseData - Respuesta del servicio V2
   * @param {number} agrupador - Código de agrupador
   * @param {string} specialtySelected - Especialidad seleccionada
   * @returns {boolean}
   */
  hasPersonalDoctor(responseData, agrupador, specialtySelected) {
    if (!responseData || !responseData.Result) {
      return false;
    }

    if (agrupador !== 1) {
      return false;
    }

    const { AvailableBranchesConsultaRed } = responseData.Result;
    if (!AvailableBranchesConsultaRed || AvailableBranchesConsultaRed.length === 0) {
      return false;
    }

    const personalDoctor = AvailableBranchesConsultaRed.find(
      (e) => e.Servicio === specialtySelected && e.Direccionamiento === 1 && e.TIDMedico === 1
    );

    return !!personalDoctor;
  }
}

export const consultaRedV2Adapter = new ConsultaRedV2Adapter();
